// API service for micro-learning backend communication
import { 
  LearningModule, 
  GetModulesResponse,
  UserModuleProgress,
  ModuleProgressResponse,
  FeedbackSubmissionRequest,
  RestartModuleRequest,
  RestartSeriesRequest,
  LearningDifficulty
} from '../components/MicroLearning/types';
import axiosInstance from './axios-instance';

export class MicroLearningAPI {
  // Module retrieval methods
  static async getModules(params: {
    category?: string;
    difficulty?: LearningDifficulty;
    search?: string;
    page?: number;
    limit?: number;
    excludeCompleted?: boolean;
  } = {}): Promise<GetModulesResponse> {
    const result = await axiosInstance.get('/api/v1/publisher/micro-learning/modules', {
      params
    });
    return result;
  }


  static async getSeriesById(id: string): Promise<any> {
    const result = await axiosInstance.get(`/api/v1/publisher/micro-learning/series/${id}`);
    return result;
  }

  static async getSeriesMetadataById(id: string): Promise<any> {
    const result = await axiosInstance.get(`/api/v1/publisher/micro-learning/series/${id}/metadata`);
    return result;
  }

  static async getModuleById(id: string): Promise<LearningModule> {
    const result = await axiosInstance.get(`/api/v1/publisher/micro-learning/modules/${id}`);
    return result;
  }

  static async getRandomModule(): Promise<LearningModule> {
    const result = await axiosInstance.get('/api/v1/publisher/micro-learning/modules/random');
    return result;
  }

  static async getModulesForFeed(limit: number = 5): Promise<any> {
    const result = await axiosInstance.get('/api/v1/publisher/micro-learning/modules/feed', {
      params: { limit }
    });
    return result;
  }

  static async getRandomModuleMetadata(): Promise<any> {
    const result = await axiosInstance.get('/api/v1/publisher/micro-learning/modules/random/metadata');
    return result;
  }

  static async getModulesByCategory(category: string): Promise<LearningModule[]> {
    const result = await axiosInstance.get(`/api/v1/publisher/micro-learning/modules/category/${encodeURIComponent(category)}`);
    return result;
  }

  static async getModulesByDifficulty(difficulty: LearningDifficulty): Promise<LearningModule[]> {
    const result = await axiosInstance.get(`/api/v1/publisher/micro-learning/modules/difficulty/${difficulty}`);
    return result;
  }

  static async searchModules(searchTerm: string): Promise<LearningModule[]> {
    const result = await axiosInstance.get(`/api/v1/publisher/micro-learning/modules/search/${encodeURIComponent(searchTerm)}`);
    return result;
  }

  // User progress methods
  static async startModule(moduleId: string): Promise<ModuleProgressResponse> {
    const result = await axiosInstance.post('/api/v1/publisher/micro-learning/progress/start', {
      moduleId
    });
    return result;
  }

  static async updateStepProgress(
    moduleId: string,
    stepId: string,
    timeSpent?: number,
    isCompleted?: boolean
  ): Promise<UserModuleProgress> {
    const result = await axiosInstance.put('/api/v1/publisher/micro-learning/progress/step', {
      moduleId,
      stepId,
      timeSpent,
      isCompleted,
    });
    return result.data;
  }

  static async completeModule(
    moduleId: string,
    finalScore?: number,
    totalTimeSpent?: number
  ): Promise<ModuleProgressResponse> {
    const result = await axiosInstance.post('/api/v1/publisher/micro-learning/progress/complete', {
      moduleId,
      finalScore,
      totalTimeSpent,
    });
    return result;
  }

  static async restartModule(request: RestartModuleRequest): Promise<ModuleProgressResponse> {
    const result = await axiosInstance.post('/api/v1/publisher/micro-learning/progress/restart', request);
    return result;
  }

  static async getUserProgress(moduleId: string): Promise<UserModuleProgress | null> {
    try {
      const result = await axiosInstance.get(`/api/v1/publisher/micro-learning/progress/${moduleId}`);
      return result;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  static async getUserHistory(): Promise<UserModuleProgress[]> {
    const result = await axiosInstance.get('/api/v1/publisher/micro-learning/progress');
    return result;
  }

  static async getUserStats(): Promise<any> {
    const result = await axiosInstance.get('/api/v1/publisher/micro-learning/stats');
    return result;
  }

  // Feedback methods
  static async submitFeedback(feedback: FeedbackSubmissionRequest): Promise<any> {
    const result = await axiosInstance.post('/api/v1/publisher/micro-learning/feedback', feedback);
    return result;
  }

  static async getUserFeedback(moduleId: string): Promise<any[]> {
    const result = await axiosInstance.get(`/api/v1/publisher/micro-learning/feedback/${moduleId}`);
    return result;
  }

  static async getModuleFeedbackSummary(moduleId: string): Promise<any> {
    const result = await axiosInstance.get(`/api/v1/publisher/micro-learning/feedback/${moduleId}/summary`);
    return result;
  }

  // Learning Preferences methods
  static async getLearningPreferences(): Promise<any> {
    try {
      const result = await axiosInstance.get('/api/v1/publisher/micro-learning/preferences');
      return result;
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Return default preferences if none exist
        return {
          categories: [],
          difficulty: 'BEGINNER',
          frequency: 3,
          timePreference: 'any'
        };
      }
      throw error;
    }
  }

  static async saveLearningPreferences(preferences: any): Promise<any> {
    const result = await axiosInstance.post('/api/v1/publisher/micro-learning/preferences', preferences);
    return result;
  }

  static async updateLearningPreferences(preferences: any): Promise<any> {
    const result = await axiosInstance.put('/api/v1/publisher/micro-learning/preferences', preferences);
    return result;
  }

  // Tutorial methods
  static async getTutorialStatus(): Promise<{ completed: boolean; completedAt: Date | null }> {
    const result = await axiosInstance.get('/api/v1/publisher/micro-learning/tutorial/status');
    return result;
  }

  static async completeTutorial(): Promise<{ success: boolean; completedAt: Date }> {
    const result = await axiosInstance.post('/api/v1/publisher/micro-learning/tutorial/complete');
    return result;
  }

  static async resetTutorial(): Promise<{ success: boolean }> {
    const result = await axiosInstance.delete('/api/v1/publisher/micro-learning/tutorial/reset');
    return result;
  }

  // Categories methods
  static async getAvailableCategories(): Promise<string[]> {
    const result = await axiosInstance.get('/api/v1/publisher/micro-learning/categories');
    return result;
  }

  // Learning Series and Paths methods
  static async getAvailableSeries(): Promise<any[]> {
    const result = await axiosInstance.get('/api/v1/publisher/micro-learning/series/available');
    return result;
  }

  static async addToLearningPath(seriesId: string, difficulty: string): Promise<any> {
    const result = await axiosInstance.post('/api/v1/publisher/micro-learning/learning-paths', {
      seriesId,
      difficulty
    });
    return result;
  }

  static async removeFromLearningPath(seriesId: string): Promise<any> {
    const result = await axiosInstance.delete(`/api/v1/publisher/micro-learning/learning-paths/${seriesId}`);
    return result;
  }

  static async getUserLearningPaths(): Promise<any[]> {
    const result = await axiosInstance.get('/api/v1/publisher/micro-learning/learning-paths');
    return result;
  }

  static async restartLearningSeries(seriesId: string, reason?: string): Promise<any> {
    const result = await axiosInstance.post(`/api/v1/publisher/micro-learning/learning-paths/${seriesId}/restart`, {
      reason
    });
    return result;
  }

  static async claimSeeds(moduleId: string, seedAmount: number, actualScore?: number, timeSpent?: number): Promise<any> {
    const result = await axiosInstance.post('/api/v1/publisher/micro-learning/claim-seeds', {
      moduleId,
      seedAmount,
      actualScore,
      timeSpent
    });
    return result;
  }

  // ===== METRICS TRACKING METHODS =====

  // Start a learning session
  static async startSession(params: {
    moduleId: string;
    deviceType?: string;
    referrer?: string;
  }): Promise<{
    sessionId: string;
    sessionNumber: number;
    progressId: string;
    resumeFromStep?: number;
  }> {
    const result = await axiosInstance.post(
      '/api/v1/publisher/micro-learning/sessions/start',
      params
    );
    return result;
  }

  // End a learning session
  static async endSession(params: {
    sessionId: string;
    moduleId: string;
    outcome: 'COMPLETED' | 'ABANDONED' | 'QUIZ_FAILED' | 'PAUSED';
    duration: number;
    endStep: number;
    stepsProgressed: number;
    quizAttempted?: boolean;
    quizScore?: number;
    quizPassed?: boolean;
  }): Promise<{ success: boolean }> {
    const result = await axiosInstance.post(
      '/api/v1/publisher/micro-learning/sessions/end',
      params
    );
    return result;
  }

  // Track module abandonment
  static async trackAbandonment(params: {
    moduleId: string;
    abandonedAtStep: number;
    timeSpent: number;
    reason?: string;
  }): Promise<{ success: boolean }> {
    const result = await axiosInstance.post(
      '/api/v1/publisher/micro-learning/progress/abandon',
      params
    );
    return result;
  }

  // Update step progress
  static async trackStepProgress(params: {
    moduleId: string;
    currentStep: number;
    timeOnStep: number;
    stepCompleted?: boolean;
  }): Promise<{ success: boolean }> {
    const result = await axiosInstance.post(
      '/api/v1/publisher/micro-learning/progress/step',
      params
    );
    return result;
  }

  // Track quiz results
  static async trackQuizResult(params: {
    moduleId: string;
    score: number;
    passed: boolean;
    correctAnswers: number;
    totalQuestions: number;
    attemptNumber: number;
    timeSpent: number;
  }): Promise<{ success: boolean }> {
    const result = await axiosInstance.post(
      '/api/v1/publisher/micro-learning/progress/quiz',
      params
    );
    return result;
  }

  // Get detailed progress with all metrics
  static async getDetailedProgress(moduleId: string): Promise<any> {
    const result = await axiosInstance.get(
      `/api/v1/publisher/micro-learning/progress/${moduleId}/detailed`
    );
    return result;
  }

  // Get user learning analytics
  static async getUserAnalytics(): Promise<any> {
    const result = await axiosInstance.get(
      '/api/v1/publisher/micro-learning/analytics/user'
    );
    return result;
  }

  // ===== INTELLIGENT INJECTION METHODS =====

  static async shouldInjectModule(params: {
    articlesScrolled: number;
    timeOnPlatform: number;
    excludeModuleIds?: string[];
  }): Promise<{
    inject: boolean;
    reason: string;
    moduleId?: string;
    module?: any;
    details?: any;
    canEarnSeeds?: boolean;
    seedsEarnedToday?: number;
    maxSeedsPerDay?: number;
    seedsPerCompletion?: number;
  }> {
    const result = await axiosInstance.post(
      '/api/v1/publisher/micro-learning/injection/should-inject',
      params
    );
    return result;
  }

  /**
   * Get a guaranteed module for a specific feed position
   * Bypasses most checks - use for position 4 on initial load
   */
  static async getGuaranteedPositionModule(position: number = 4): Promise<{
    inject: boolean;
    reason: string;
    moduleId?: string;
    module?: any;
    details?: any;
    canEarnSeeds?: boolean;
    seedsEarnedToday?: number;
    maxSeedsPerDay?: number;
    seedsPerCompletion?: number;
  }> {
    const result = await axiosInstance.get(
      `/api/v1/publisher/micro-learning/injection/guaranteed/${position}`
    );
    return result;
  }

  static async recordInjection(params: {
    moduleId: string;
    articlesScrolled: number;
    timeOnPlatform: number;
    injectionReason: string;
  }): Promise<{ attemptId: string }> {
    const result = await axiosInstance.post(
      '/api/v1/publisher/micro-learning/injection/record',
      params
    );
    return result;
  }

  static async updateInjectionAttempt(
    attemptId: string,
    updates: {
      wasStarted?: boolean;
      wasCompleted?: boolean;
      quizPassed?: boolean;
      abandoned?: boolean;
      timeSpent?: number;
      score?: number;
    }
  ): Promise<{ success: boolean }> {
    const result = await axiosInstance.put(
      `/api/v1/publisher/micro-learning/injection/attempt/${attemptId}`,
      { attemptId, ...updates }
    );
    return result;
  }

  static async getInjectionStats(): Promise<{
    totalShown: number;
    totalStarted: number;
    totalCompleted: number;
    totalAbandoned: number;
    conversionRate: number;
    completionRate: number;
    passRate: number;
    averageTimeSpent: number;
    averageScore: number;
  }> {
    const result = await axiosInstance.get('/api/v1/publisher/micro-learning/injection/stats');
    return result;
  }

  static async getInjectionConfig(): Promise<{
    id: string;
    cooldownAfterPass: number;
    cooldownAfterFail: number;
    cooldownAfterAbandon: number;
    maxModulesShownPerDay: number;
    maxModulesCompletedPerDay: number;
    minArticlesScrolled: number;
    minTimeOnPlatform: number;
    enabled: boolean;
  } | null> {
    const result = await axiosInstance.get('/api/v1/publisher/micro-learning/injection/config');
    return result;
  }

  static async updateInjectionConfig(updates: {
    cooldownAfterPass?: number;
    cooldownAfterFail?: number;
    cooldownAfterAbandon?: number;
    maxModulesShownPerDay?: number;
    maxModulesCompletedPerDay?: number;
    minArticlesScrolled?: number;
    minTimeOnPlatform?: number;
    enabled?: boolean;
  }): Promise<any> {
    const result = await axiosInstance.put(
      '/api/v1/publisher/micro-learning/injection/config',
      updates
    );
    return result;
  }
}

// Legacy functions for backward compatibility (can be removed later)
export const getRandomModule = (): Promise<LearningModule> => {
  return MicroLearningAPI.getRandomModule();
};

export const getModulesByCategory = (category: string): Promise<LearningModule[]> => {
  return MicroLearningAPI.getModulesByCategory(category);
};

export const getModulesByDifficulty = (difficulty: LearningDifficulty): Promise<LearningModule[]> => {
  return MicroLearningAPI.getModulesByDifficulty(difficulty);
};

export const getModuleById = (id: string): Promise<LearningModule> => {
  return MicroLearningAPI.getModuleById(id);
};

export const getPublishedModules = (): Promise<LearningModule[]> => {
  return MicroLearningAPI.getModules().then(response => response.modules);
};

export const searchModulesByTopic = (searchTerm: string): Promise<LearningModule[]> => {
  return MicroLearningAPI.searchModules(searchTerm);
};

export default MicroLearningAPI;
